import * as Dialog from '@radix-ui/react-dialog';
import { Play } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useScope } from '@/auth/useScope';
import { useAppStore } from '@/app/appStore';
import { usePanelStore } from '@/panels/panelStore';
import { useMobileWorkspaceStore } from '@/panels/mobileWorkspaceStore';
import {
  buildLayoutConfigFromPanels,
  hydratePanelsFromLayoutConfig,
  LAYOUT_TEMPLATE_SURFACE,
  parseLayoutConfig,
} from './layoutTemplates';
import { useLayoutTemplates, useSaveTemplate } from './useTemplates';

type PendingLayoutLoad = {
  templateId: string;
  templateName: string;
  config: unknown;
};

export function WorkspaceLayoutDialogs() {
  const { t } = useTranslation('common');
  const canRead = useScope('workbench:templates:read');
  const canWrite = useScope('workbench:templates:write');

  const panels = usePanelStore((s) => s.panels);
  const replaceWorkspacePanels = usePanelStore((s) => s.replaceWorkspacePanels);
  const clearMobileStack = useMobileWorkspaceStore((s) => s.clearStack);
  const pushMobilePanel = useMobileWorkspaceStore((s) => s.pushPanel);

  const saveOpen = useAppStore((s) => s.layoutSaveDialogOpen);
  const loadOpen = useAppStore((s) => s.layoutLoadDialogOpen);
  const suggestedName = useAppStore((s) => s.layoutSuggestedName);
  const setSaveOpen = useAppStore((s) => s.setLayoutSaveDialogOpen);
  const setLoadOpen = useAppStore((s) => s.setLayoutLoadDialogOpen);
  const setLastAgentAction = useAppStore((s) => s.setLastAgentAction);

  const save = useSaveTemplate();
  const layouts = useLayoutTemplates({ enabled: loadOpen && canRead });

  const [name, setName] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pendingLoad, setPendingLoad] = useState<PendingLayoutLoad | null>(null);

  const nonDetachedPanels = useMemo(
    () => panels.filter((panel) => !panel.detached),
    [panels],
  );

  useEffect(() => {
    if (saveOpen) {
      setName(suggestedName);
      setSaveError(null);
    }
  }, [saveOpen, suggestedName]);

  useEffect(() => {
    if (!loadOpen) {
      setLoadError(null);
      setPendingLoad(null);
    }
  }, [loadOpen]);

  async function handleSaveLayout(e: React.FormEvent) {
    e.preventDefault();
    if (!canWrite) return;

    const trimmedName = name.trim();
    if (!trimmedName) {
      setSaveError(t('layouts.nameRequired', { defaultValue: 'Name is required.' }));
      return;
    }

    const config = buildLayoutConfigFromPanels(nonDetachedPanels);
    try {
      await save.mutateAsync({
        name: trimmedName,
        surface: LAYOUT_TEMPLATE_SURFACE,
        config,
      });
      setLastAgentAction({
        correlationId: 'layout-save',
        description: t('layouts.saved', { defaultValue: 'Layout saved.' }),
        timestamp: new Date().toISOString(),
      });
      setSaveOpen(false);
    } catch (err) {
      setSaveError(
        err instanceof Error
          ? err.message
          : t('layouts.saveError', { defaultValue: 'Could not save layout.' }),
      );
    }
  }

  function syncWorkspacePanels(templateId: string, description: string, panelsToOpen: ReturnType<typeof hydratePanelsFromLayoutConfig>['panels']) {
    replaceWorkspacePanels(panelsToOpen);
    clearMobileStack();
    for (const panel of panelsToOpen) {
      pushMobilePanel(panel.panelId);
    }
    setLastAgentAction({
      correlationId: `layout-load-${templateId}`,
      description,
      timestamp: new Date().toISOString(),
    });
    setLoadError(null);
    setPendingLoad(null);
    setLoadOpen(false);
  }

  function handleLoadTemplate(templateId: string, templateName: string, rawConfig: unknown) {
    if (!canRead) return;

    const parsed = parseLayoutConfig(rawConfig);
    if (!parsed) {
      setPendingLoad(null);
      setLoadError(t('layouts.invalidConfig', { defaultValue: 'Invalid layout payload.' }));
      return;
    }

    if (nonDetachedPanels.length > 0 && pendingLoad?.templateId !== templateId) {
      setPendingLoad({ templateId, templateName, config: rawConfig });
      return;
    }

    const hydrated = hydratePanelsFromLayoutConfig(parsed);
    const droppedSurfaces = [...new Set([
      ...hydrated.droppedUnknownSurfaces,
      ...hydrated.droppedInvalidPayloads,
    ])];

    for (const surface of hydrated.droppedUnknownSurfaces) {
      console.warn('[workspace-layout] dropped unknown surface from layout template', surface);
    }
    for (const surface of hydrated.droppedInvalidPayloads) {
      console.warn('[workspace-layout] dropped invalid payload from layout template', surface);
    }

    if (hydrated.panels.length === 0) {
      setPendingLoad(null);
      setLoadError(
        t('layouts.noValidPanels', {
          defaultValue: 'This layout has no valid panels to restore.',
        }),
      );
      return;
    }

    if (droppedSurfaces.length > 0) {
      syncWorkspacePanels(
        templateId,
        t('layouts.loadedPartialNamed', {
          defaultValue:
            'Layout "{{name}}" restored {{count}} surfaces. Dropped: {{surfaces}}.',
          name: templateName,
          count: hydrated.panels.length,
          surfaces: droppedSurfaces.join(', '),
        }),
        hydrated.panels,
      );
      return;
    }

    syncWorkspacePanels(
      templateId,
      t('layouts.loadedCountNamed', {
        defaultValue: 'Layout "{{name}}" restored {{count}} surfaces.',
        name: templateName,
        count: hydrated.panels.length,
      }),
      hydrated.panels,
    );
  }

  return (
    <>
      <Dialog.Root open={saveOpen} onOpenChange={setSaveOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm" />
          <Dialog.Content
            className="fixed left-1/2 top-[20%] z-50 w-[92%] max-w-md -translate-x-1/2 rounded-lg border border-border bg-card p-5 text-card-foreground shadow-wb-lg"
            aria-describedby={undefined}
            data-testid="save-layout-dialog"
          >
            <Dialog.Title className="text-md font-semibold">
              {t('layouts.saveTitle', { defaultValue: 'Save workspace layout' })}
            </Dialog.Title>
            <form onSubmit={(e) => void handleSaveLayout(e)} className="mt-4 space-y-4">
              <div className="space-y-1">
                <label
                  htmlFor="workspace-layout-name"
                  className="block text-xs uppercase text-muted-foreground"
                >
                  {t('layouts.nameLabel', { defaultValue: 'Name' })}
                </label>
                <input
                  id="workspace-layout-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('layouts.namePlaceholder', { defaultValue: 'e.g. EOD desk' })}
                  className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  autoFocus
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {t('layouts.tileCount', {
                  defaultValue: '{{count}} panels will be saved.',
                  count: nonDetachedPanels.length,
                })}
              </p>
              {saveError && (
                <p role="alert" className="text-sm text-destructive">
                  {saveError}
                </p>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setSaveOpen(false)}
                  className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted"
                >
                  {t('layouts.cancel', { defaultValue: 'Cancel' })}
                </button>
                <button
                  type="submit"
                  disabled={save.isPending}
                  className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  {t('layouts.save', { defaultValue: 'Save layout' })}
                </button>
              </div>
            </form>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <Dialog.Root open={loadOpen} onOpenChange={setLoadOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm" />
          <Dialog.Content
            className="fixed left-1/2 top-[16%] z-50 w-[92%] max-w-lg -translate-x-1/2 rounded-lg border border-border bg-card p-5 text-card-foreground shadow-wb-lg"
            aria-describedby={undefined}
            data-testid="load-layout-dialog"
          >
            <Dialog.Title className="text-md font-semibold">
              {t('layouts.loadTitle', { defaultValue: 'Load workspace layout' })}
            </Dialog.Title>

            <div className="mt-4 max-h-[55vh] overflow-y-auto rounded border border-border">
              {layouts.isLoading && (
                <div className="p-3 text-sm text-muted-foreground">
                  {t('layouts.loading', { defaultValue: 'Loading…' })}
                </div>
              )}
              {layouts.isError && (
                <div role="alert" className="p-3 text-sm text-destructive">
                  {t('layouts.loadError', { defaultValue: 'Could not load layouts.' })}
                </div>
              )}
              {!layouts.isLoading && !layouts.isError && (layouts.data ?? []).length === 0 && (
                <div className="p-3 text-sm text-muted-foreground">
                  {t('layouts.empty', { defaultValue: 'No saved layouts yet.' })}
                </div>
              )}
              <ul>
                {(layouts.data ?? []).map((layout) => (
                  <li
                    key={layout.template_id}
                    className="border-b border-border px-3 py-2 last:border-b-0"
                  >
                    <div className="flex items-center gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm">{layout.name}</p>
                        <p className="truncate text-xs text-muted-foreground">{layout.updated_at}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleLoadTemplate(layout.template_id, layout.name, layout.config)}
                        className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs hover:bg-muted"
                        data-testid={`layout-template-open-${layout.template_id}`}
                      >
                        <Play size={12} />
                        {pendingLoad?.templateId === layout.template_id
                          ? t('layouts.confirmOpen', { defaultValue: 'Confirm' })
                          : t('layouts.open', { defaultValue: 'Open' })}
                      </button>
                    </div>
                    {pendingLoad?.templateId === layout.template_id ? (
                      <div className="mt-2 rounded-md border border-[color:var(--wb-warning)] bg-[color:var(--wb-warning-muted)] p-2 text-xs text-[color:var(--wb-warning)]">
                        <p>
                          {t('layouts.confirmReplace', {
                            defaultValue:
                              'Loading this layout will close all current panels. Continue?',
                          })}
                        </p>
                        <div className="mt-2 flex gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              handleLoadTemplate(
                                pendingLoad.templateId,
                                pendingLoad.templateName,
                                pendingLoad.config,
                              )
                            }
                            className="rounded border border-current px-2 py-1 hover:bg-background/60"
                          >
                            {t('layouts.confirmOpen', { defaultValue: 'Confirm' })}
                          </button>
                          <button
                            type="button"
                            onClick={() => setPendingLoad(null)}
                            className="rounded border border-current px-2 py-1 hover:bg-background/60"
                          >
                            {t('layouts.cancel', { defaultValue: 'Cancel' })}
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>

            {loadError && (
              <p role="alert" className="mt-3 text-sm text-destructive">
                {loadError}
              </p>
            )}

            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setLoadOpen(false)}
                className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted"
              >
                {t('layouts.cancel', { defaultValue: 'Cancel' })}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}
